import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
// CategoryPage.tsx — Co Marca Admin — estilo AboutusProjects
import Loading from '../../../components/Loading';
import React, { useEffect, useState, useCallback } from 'react';
import { Edit, Trash2, Plus, History, Store, ChevronRight, Home } from 'lucide-react';
import { fetchCategories } from '../../../services/categoryService';
import CategoryModal from '../../../components/Category/CategoryModal';
import EditCategoryModal from '../../../components/Category/EditCategoryModal';
import Pagination from '../../../components/Pagination/Pagination';
import DeleteCategoryModal from '../../../components/Category/DeleteCategoryModal';
import ExpiredSessionModal from '../../../components/Expired/ExpiredSessionModal';
import { useSessionExpiration } from '../../../hooks/useSessionExpiration';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useModelPermission } from '../../../hooks/useModelPermission';

interface SucursalPreview {
    id: number;
    nombre: string;
    codigo: string;
}

interface Category {
    id: number;
    nombre: string;
    descripcion?: string;
    imagen?: string;
    estado: boolean;
    fecha_creacion: string;
    sucursales_details?: SucursalPreview[];
}

const colors = {
    primary:        'var(--ad-primary, #3564db)',
    primaryDark:    'var(--ad-primary-dark, #8f1f64)',
    secondary:      'var(--ad-secondary, #3564db)',
    secondaryLight: 'var(--ad-secondary, #92b1ed)',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `


.cat-page { font-family: 'DM Sans', sans-serif; }

@keyframes sectionFadeUp {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
    0%,100% { box-shadow:0 0 0 0 rgba(var(--ad-primary-rgb, 193, 52, 139),.35); }
    50%     { box-shadow:0 0 0 5px rgba(var(--ad-primary-rgb, 193, 52, 139),0); }
}
@keyframes blobFloat {
    0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
    50%     { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}

.cat-fadeup   { animation: sectionFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.cat-fadeup-1 { animation: sectionFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }

.cat-btn-primary {
    display:inline-flex; align-items:center; gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff; border:none; border-radius:11px;
    padding:9px 20px; font-size:.83rem; font-weight:800;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(var(--ad-primary-rgb, 193, 52, 139),.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);
    white-space:nowrap;
}
.cat-btn-primary:hover { transform:translateY(-2px); box-shadow:0 8px 22px rgba(var(--ad-primary-rgb, 193, 52, 139),.32); }

.cat-btn-secondary {
    display:inline-flex; align-items:center; gap:7px;
    background:var(--ad-surface, #fff); border:1.5px solid var(--ad-line, #e2e8f0);
    color:var(--ad-ink, #374151); border-radius:11px;
    padding:9px 16px; font-size:.83rem; font-weight:700;
    cursor:pointer; font-family:'DM Sans',sans-serif;
    transition:all .2s;
}
.cat-btn-secondary:hover { background:var(--ad-soft, #f8fafc); border-color:color-mix(in srgb, ${colors.secondary} 25.1%, transparent); color:var(--ad-link, #2854c5); }

.cat-table-row { transition:background .15s; }
.cat-table-row:hover { background:var(--ad-soft, #fafcff); }

.cat-action-btn {
    width:30px; height:30px; border-radius:9px; border:none;
    display:inline-flex; align-items:center; justify-content:center;
    cursor:pointer; transition:all .2s;
}

.custom-scroll::-webkit-scrollbar { height:6px; width:6px; }
.custom-scroll::-webkit-scrollbar-track { background:transparent; }
.custom-scroll::-webkit-scrollbar-thumb { background-color:rgba(97, 37, 123,0.3); border-radius:20px; }
.custom-scroll::-webkit-scrollbar-thumb:hover { background-color:rgba(97, 37, 123,0.5); }
`;

const CategoryPage: React.FC = () => {
    const [categories, setCategories]           = useState<Category[]>([]);
    const [isModalOpen, setIsModalOpen]         = useState(false);
    const [editModalOpen, setEditModalOpen]     = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { logout, user } = useAuth();
    const navigate = useNavigate();
    const [currentPage, setCurrentPage]         = useState(1);
    const itemsPerPage = 10;
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('category', userLevelId);

    const [isImagePreviewOpen, setIsImagePreviewOpen] = useState(false);
    const [previewImageUrl, setPreviewImageUrl]       = useState<string | null>(null);

    const loadCategories = useCallback(async () => {
        try {
            if (perm?.can_read) {
                const data = await fetchCategories();
                setCategories(data.sort((a: Category, b: Category) => a.id - b.id));
            } else {
                setCategories([]);
            }
        } catch (error) {
            /* Browser diagnostics omitted. */
            setCategories([]);
        }
    }, [perm]);

    useEffect(() => { loadCategories(); }, [loadCategories]);

    const totalPages        = Math.ceil(categories.length / itemsPerPage);
    const startIndex        = (currentPage - 1) * itemsPerPage;
    const displayedCategories = categories.slice(startIndex, startIndex + itemsPerPage);

    const handleImagePreview = (url: string) => { setPreviewImageUrl(url); setIsImagePreviewOpen(true); };
    const handleEditClick    = (cat: Category) => { setSelectedCategory(cat); setEditModalOpen(true); };
    const handleDeleteClick  = (cat: Category) => { setCategoryToDelete(cat); setDeleteModalOpen(true); };

    if (perm === null) return <Loading text="Cargando categorías..." />;

    return <><LumaResourceWorkspace title="Categorías" description="Lista completa de categorías" group="Servicios" rows={categories} columns={[{key:'nombre',label:'Nombre'},{key:'descripcion',label:'Descripción'},{key:'estado',label:'Estado'}]} idKey="id" nameKey="nombre" statusKey="estado" canRead={perm?.can_read} onCreate={perm?.can_create?()=>{setIsModalOpen(true)}:undefined} onEdit={perm?.can_update?handleEditClick:undefined} onDelete={perm?.can_delete?handleDeleteClick:undefined} history="/categories/history"/><CategoryModal    isOpen={isModalOpen}    onClose={()=>setIsModalOpen(false)}    onCreated={loadCategories}/><EditCategoryModal isOpen={editModalOpen}  onClose={()=>setEditModalOpen(false)}  category={selectedCategory} onUpdated={loadCategories}/><DeleteCategoryModal isOpen={deleteModalOpen} onClose={()=>setDeleteModalOpen(false)} category={categoryToDelete} onDeleted={loadCategories}/><ExpiredSessionModal isOpen={isSessionExpired} onClose={async()=>{ setIsSessionExpired(false); await logout(); }}/></>;
};

export default CategoryPage;
